import QueryBuilder, {QueryBuilderUtils} from "../../../../../../packages/query-builder";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import useDynamicColumns, {DynamicColumnApplications} from "../../../../../hooks/use-dynamic-columns";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {numComparator} from "../../../../../../core/services/utils/utils";
import Api from "../../../../../../core/services/api/api";
import LoadingLottieAnimation from "../../../../../components/app-specific/lottie-animations/loading";
import {Box, Skeleton, Typography} from "@mui/material";
import useSearch from "../../../../../hooks/use-search";
import NCRTasksViewColumn from "./column";


const dynamicColumnsSectionName = 'Task';

const NCRTasks = () => {
    const [statuses, setStatuses] = useState([]);
    const [loadingStatuses, setLoadingStatuses] = useState(true);
    const [loadingData, setLoadingData] = useState({});
    const [changingTaskStatus, setChangingTaskStatus] = useState(false);
    const isMounted = useIsMounted();

    const queryBuilderSaveName = useMemo(() => 'NCRTasksListViewQueryBuilder', [])
    const queryBuilderLoading = useMemo(() => Object.values(loadingData).some(e => e), [loadingData])

    const dataRef = useRef({});

    const staticDataGridColumns = useMemo(() => [], []);

    const dataGridColumnsConfig = useMemo(() => ({}), [])

    const [dynamicColumnsLoading, queryBuilderColumns] = useDynamicColumns({
        sectionName: dynamicColumnsSectionName,
        staticDataGridColumns: staticDataGridColumns,
        dataGridColumnsConfig: dataGridColumnsConfig,
        applicationName: DynamicColumnApplications.BizTaskService,
    });

    /**
     * Fetches the statuses of the ncr Tasks of the system.
     */
    const getStatuses = useCallback(async () => {
        setLoadingStatuses(true);
        const response = await Api.getTaskStatuses();
        if (!isMounted())
            return;
        setLoadingStatuses(false);
        if (response?.resultFlag) {
            const statuses = response.data ?? [];
            statuses.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
            setStatuses(statuses);
            setLoadingData(Object.fromEntries(response.data?.map(status => [status.id, false]) ?? []));
        }
    }, [isMounted]);

    /**
     * As soon as the component mounts:
     * - fetches the list of statuses of tasks
     */
    useEffect(() => {
        void getStatuses()
    }, [getStatuses]);

    /**
     * Changes the status of a task based on the drag event.
     */
    const changeTaskStatus = useCallback(async (evt, task) => {
        const {newDraggableIndex, oldDraggableIndex, from, to} = evt;
        if (from === to)
            return;

        const statusOfFrom = statuses.find(s => `task-status-${s.id}` === from.id);
        const statusOfTo = statuses.find(s => `task-status-${s.id}` === to.id);

        setChangingTaskStatus(true);

        const {setData: setFromData, setPaginationInfo: setFromPaginationInfo} = dataRef.current?.[statusOfFrom.id.toString()] ?? {};
        const {setData: setToData, setPaginationInfo: setToPaginationInfo} = dataRef.current?.[statusOfTo.id.toString()] ?? {};

        setFromData?.((prevState) => prevState.filter((e) => e.id !== task.id));
        setToData?.((prevState) => {
            const newState = [...prevState];
            newState.splice(newDraggableIndex, 0, task);
            return newState;
        });
        const forApi = {
            taskId: task?.id,
            taskStatusId: statusOfTo.id,
        }
        const response = await Api.changeTaskStatus(forApi)
        if (!isMounted())
            return;
        setChangingTaskStatus(false);
        if (!response?.resultFlag) {
            setFromData?.((prevState) => {
                const newState = [...prevState];
                newState.splice(oldDraggableIndex, 0, task);
                return newState;
            });
            setToData?.((prevState) => prevState.filter((e) => e.id !== task.id));
        } else {
            setFromPaginationInfo?.((prevState) => ({...prevState, total: prevState.length - 1}));
            setToPaginationInfo?.((prevState) => ({...prevState, total: prevState.length + 1}));
        }
    }, [isMounted, statuses])

    /**
     * Sets the loading status of a column.
     */
    const setColumnLoading = useCallback((statusId, loading) => {
        setLoadingData(prevState => ({
            ...prevState,
            [statusId]: loading,
        }))
    }, [])

    const searchData = useSearch(async () => void 0, {filters: {}})

    const apiQuery = useMemo(() => QueryBuilderUtils.getApiQueryFromStateQuery(searchData.filters, queryBuilderColumns), [searchData.filters, queryBuilderColumns])

    return (
        <>
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', flexWrap: 'wrap', gap: 2}}>
                <Typography variant={'subtitle1'} fontWeight={600}>
                    NCR Tasks List
                </Typography>
            </Box>
            {
                dynamicColumnsLoading &&
                <Box sx={{marginBlock: 25}}>
                    <LoadingLottieAnimation width={'50%'}/>
                </Box>
            }
            {
                !dynamicColumnsLoading &&
                <>
                    <QueryBuilder
                        query={searchData.filters}
                        search={searchData.setFilters}
                        columns={queryBuilderColumns}
                        loading={queryBuilderLoading}
                        name={queryBuilderSaveName}
                    />
                    <Box sx={{display: 'flex', overflowX: 'auto', gap: 3, paddingBlockEnd: 1,}} className={'ncr-tasks-view'}>
                        {
                            loadingStatuses &&
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    key={key}
                                    width={350}
                                    height={'80vh'}
                                    sx={{minWidth: 350, transform: 'none', transformOrigin: 'none',}}
                                />
                            ))
                        }
                        {
                            !loadingStatuses &&
                            statuses.map(status => (
                                <NCRTasksViewColumn
                                    key={status.id}
                                    ref={dataRef}
                                    query={apiQuery}
                                    status={status}
                                    moveTask={changeTaskStatus}
                                    movingTask={changingTaskStatus}
                                    setLoading={setColumnLoading}
                                    statuses={statuses}
                                />
                            ))
                        }
                        {
                            !loadingStatuses && statuses.length === 0 &&
                            <Box marginBlock={10} sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%'}}>
                                <Typography variant={'body2'} textAlign={'center'} marginBlockEnd={2}>
                                    You have no task statuses. Please create one first in the notebook view.
                                </Typography>
                            </Box>

                        }
                    </Box>
                </>
            }
        </>
    );
}

export default NCRTasks;
